import { CharacterState, characterStateOf } from "../combat/anim.ts";
import { EntityKind } from "../protocol/kinds.ts";
import { NO_NAME, type EntityName } from "../protocol/names.ts";
import { PvpFlag, flagStateOf } from "../zones/zones.ts";
import { smallTextBoxWidth, smallTextWidth } from "../ui/small-text.ts";
import type { ColourRoleName, PaletteName } from "../ui/tokens.ts";
import { inMinimapVision } from "./minimap.ts";

/**
 * The minimap's marks of other players and creatures (P1.9-T179; ADR-061's note of 2026-10-14 under decision 6): pure
 * data and rules, so the page and its tests read one. An entity is marked only while the page draws it and its feet lie
 * inside the player's vision rectangle (`inMinimapVision`), never for one in the interest area's margin beyond it, one
 * that is dead, or one the world view hides from this player. The marks live in one preallocated buffer the frame
 * refills (`MinimapOthers`), so drawing them allocates nothing, and a signature of what the buffer holds tells the
 * painters whether anything moved.
 */

/**
 * The most marks the buffer holds. The vision rectangle is 27 by 15 tiles, about 400, so a crowd the game allows fits;
 * an entity beyond the capacity is left unmarked and counted in `dropped`.
 */
export const MINIMAP_OTHERS_CAPACITY = 512;

/**
 * The most names a field draws at once: the nearest players' names that do not meet, at most this many.
 */
export const MINIMAP_NAMES_MOST = 24;

/**
 * What an entity marked on the minimap is: another player (`Player`, or `FlaggedPlayer` while their PvP flag is on), or
 * a creature or character of one of the kinds the author's table of 2026-10-14 tells apart. Today a template can make
 * `Neutral`, `Aggressive`, `Elite` and `Boss`; `Npc`, `Passive`, `Quest` and `QuestTarget` arrive with the NPC rows and
 * the quest tracker, and nothing yet produces them.
 */
export const MinimapOtherKind = { Player: 0, Npc: 1, Passive: 2, Quest: 3, QuestTarget: 4, Neutral: 5, Aggressive: 6, Elite: 7, Boss: 8, FlaggedPlayer: 9 } as const;
export type MinimapOtherKind = (typeof MinimapOtherKind)[keyof typeof MinimapOtherKind];

/**
 * How many kinds there are.
 */
export const MINIMAP_OTHER_KINDS = 10;

/**
 * What a mark is drawn as: a round dot, a star or a skull.
 */
export type MinimapShape = "dot" | "star" | "skull";

/**
 * How one kind is drawn and shown: the palette token that colours it, its shape, and whether it is marked only within
 * `MINIMAP_NEAR_TILES` of the player (inside vision, which still bounds everything) or anywhere inside vision. A kind
 * with an `outline` draws a one pixel outline in that token at every zoom, in the HUD as well as in Nearby, and one
 * with a `cross` has a red cross over the dot, drawn in that token.
 */
export interface MinimapOtherLook {
  readonly token: PaletteName | ColourRoleName;
  readonly shape: MinimapShape;
  readonly near: boolean;
  readonly outline?: PaletteName | ColourRoleName;
  readonly cross?: PaletteName | ColourRoleName;
}

/**
 * The colour table of the author's ruling of 2026-10-14, by kind (the tokens are drafts for the author): `npc` and
 * `passive` white (`text`), `quest` a yellow dot and `questTarget` a yellow star (`warning`), `neutral` orange
 * (`infamy-suspect`, the nearest token to orange), `aggressive` red (`danger`), `boss` and `elite` a red skull
 * (`danger`). Other players are a blue diamond (`info`, a blue that is not the player's own `focus-res`) with a white
 * (`text`) outline, and a player flagged for PvP the same with a red (`danger`) cross over it (ADR-061's note of
 * 2026-10-14, P1.9-T180, the author's choice). Bosses, elites and players show anywhere inside vision; the rest only
 * near.
 */
export const MINIMAP_OTHER_LOOKS: readonly MinimapOtherLook[] = [
  { token: "info", shape: "dot", near: false, outline: "text" },
  { token: "text", shape: "dot", near: true },
  { token: "text", shape: "dot", near: true },
  { token: "warning", shape: "dot", near: true },
  { token: "warning", shape: "star", near: true },
  { token: "infamy-suspect", shape: "dot", near: true },
  { token: "danger", shape: "dot", near: true },
  { token: "danger", shape: "skull", near: false },
  { token: "danger", shape: "skull", near: false },
  { token: "info", shape: "dot", near: false, outline: "text", cross: "danger" },
];

/**
 * How far, in tiles between feet points, a mark that is not a player, an elite or a boss is shown (the author's ruling
 * of 2026-10-14). Nothing is ever shown outside the vision rectangle.
 */
export const MINIMAP_NEAR_TILES = 10;

/**
 * What `minimapOtherKindOf` answers for an entity that gets no mark.
 */
export const NO_MINIMAP_MARK = -1;

/**
 * The height of a name's box, in logical pixels: the small pixel face's line, 9 rows: the capitals' 6, the descender row
 * and the one pixel outline above and below.
 */
const NAME_HEIGHT = 9;

/**
 * The advance of the space between a first name and a family name.
 */
const SMALL_SPACE = smallTextWidth(" ");
const NAME_GAP = 4;
const MARK_BOX = 7;
const FLIP_AFTER = 0.55;
const SEED = 0x811c9dc5 | 0;

/**
 * The marks of the frame: the handle, kind and position in tiles of each entity marked, in the order they were added,
 * its name for the Nearby window, and a signature of what is held. The arrays are fixed at the capacity; `count` says
 * how many entries are live.
 */
export interface MinimapOthers {
  count: number;
  dropped: number;
  signature: number;
  readonly handle: Int32Array;
  readonly kind: Uint8Array;
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly name: EntityName[];
}

/**
 * Where the names of a field lie: for each name drawn, the entry of the marks it names, the edge it hangs from (its
 * left edge, or its right edge when flipped), its top and whether it is flipped to the left of its mark. The arrays
 * are fixed at `MINIMAP_NAMES_MOST`.
 */
export interface MinimapNames {
  count: number;
  readonly index: Int32Array;
  readonly anchor: Int16Array;
  readonly top: Int16Array;
  readonly flipped: Uint8Array;
  readonly scratchIndex: Int32Array;
  readonly scratchDistance: Float64Array;
}

/**
 * Makes an empty buffer of marks.
 *
 * @param capacity How many marks it holds; `MINIMAP_OTHERS_CAPACITY` unless given.
 * @returns The buffer.
 */
export function createMinimapOthers(capacity: number = MINIMAP_OTHERS_CAPACITY): MinimapOthers {
  return {
    count: 0,
    dropped: 0,
    signature: SEED,
    handle: new Int32Array(capacity),
    kind: new Uint8Array(capacity),
    x: new Float32Array(capacity),
    y: new Float32Array(capacity),
    name: new Array<EntityName>(capacity).fill(NO_NAME),
  };
}

/**
 * Makes an empty set of name places.
 *
 * @returns The set.
 */
export function createMinimapNames(): MinimapNames {
  return {
    count: 0,
    index: new Int32Array(MINIMAP_NAMES_MOST),
    anchor: new Int16Array(MINIMAP_NAMES_MOST),
    top: new Int16Array(MINIMAP_NAMES_MOST),
    flipped: new Uint8Array(MINIMAP_NAMES_MOST),
    scratchIndex: new Int32Array(MINIMAP_NAMES_MOST),
    scratchDistance: new Float64Array(MINIMAP_NAMES_MOST),
  };
}

/**
 * Empties a buffer for the next frame, keeping its arrays.
 *
 * @param others The buffer.
 */
export function resetMinimapOthers(others: MinimapOthers): void {
  others.count = 0;
  others.dropped = 0;
  others.signature = SEED;
}

/**
 * How a creature is marked, from what its look table says: an elite or a boss is a skull whatever its temperament,
 * and any other is orange when neutral and red when aggressive.
 *
 * @param temperament The look's temperament.
 * @param rank The look's rank.
 * @returns The mark's kind.
 */
export function minimapCreatureKindOf(temperament: string, rank: string): number {
  if (rank === "boss") {
    return MinimapOtherKind.Boss;
  }
  if (rank === "elite") {
    return MinimapOtherKind.Elite;
  }
  return temperament === "neutral" ? MinimapOtherKind.Neutral : MinimapOtherKind.Aggressive;
}

/**
 * An NPC's mark kind from its look's plate colour (ADR-136 decision 2.4; the author's minimap rules of 2026-10-14,
 * "White for npcs"): a non-combatant or a friendly fighter is an NPC's white dot, a neutral one the neutral mark and
 * a hostile one the aggressive mark, so an NPC that attacks on sight reads as a creature that does.
 *
 * @param plate The plate colour its look carries: `white`, `gold`, `red` or `green`.
 * @returns The mark's kind.
 */
export function minimapNpcKindOf(plate: string): number {
  return plate === "red" ? MinimapOtherKind.Aggressive : plate === "gold" ? MinimapOtherKind.Neutral : MinimapOtherKind.Npc;
}

/**
 * Whether a mark's kind is a player's, flagged or not.
 *
 * @param kind A mark's kind.
 * @returns True for `Player` and `FlaggedPlayer`.
 */
export function isMinimapPlayer(kind: number): boolean {
  return kind === MinimapOtherKind.Player || kind === MinimapOtherKind.FlaggedPlayer;
}

/**
 * A player's mark kind from their flag byte, the `pvp` of the entity's state (`PvpFlag`: the same byte the nameplate
 * draws and `mayHarm` reads): flagged when its state bits say Flagged or Combatant, whatever the switching bit and the
 * zone lock say, since such a player may be harmed; a player otherwise.
 *
 * @param flag The flag byte.
 * @returns `MinimapOtherKind.FlaggedPlayer` or `MinimapOtherKind.Player`.
 */
export function minimapPlayerKindOf(flag: number): number {
  const state = flagStateOf(flag);
  return state === PvpFlag.Flagged || state === PvpFlag.Combatant ? MinimapOtherKind.FlaggedPlayer : MinimapOtherKind.Player;
}

/**
 * Whether an entity of a kind, in an animation state, gets a mark, and which. Only players, creatures and NPCs
 * (ADR-136) are marked; a dead one is not, and neither is one the world view hides from this player (`hidden`: concealed behind a prop or under
 * a roof the viewer is not under, and not the viewer's target).
 *
 * @param entityKind The entity's kind (`EntityKind`).
 * @param anim Its animation bits.
 * @param hidden Whether the world view hides it from this player.
 * @param creature The mark kind of a creature (`minimapCreatureKindOf`) or of an NPC (`minimapNpcKindOf`), or
 *   `NO_MINIMAP_MARK` when its look is not known, which leaves it unmarked.
 * @param pvp The entity's PvP flag byte, which tells a flagged player from another (`minimapPlayerKindOf`).
 * @returns The mark's kind, or `NO_MINIMAP_MARK`.
 */
export function minimapOtherKindOf(entityKind: number, anim: number, hidden: boolean, creature: number, pvp: number): number {
  if (hidden || characterStateOf(anim) === CharacterState.Dead) {
    return NO_MINIMAP_MARK;
  }
  if (entityKind === EntityKind.Player) {
    return minimapPlayerKindOf(pvp);
  }
  if (entityKind === EntityKind.Creature || entityKind === EntityKind.Npc) {
    return creature;
  }
  return NO_MINIMAP_MARK;
}

/**
 * Which kinds a buffer holds, for the Nearby legend.
 *
 * @param others The buffer.
 * @returns A bit set, bit `k` set when a mark of kind `k` is held.
 */
export function minimapKindsPresent(others: MinimapOthers): number {
  let present = 0;
  for (let i = 0; i < others.count; i += 1) {
    present |= 1 << (others.kind[i] as number);
  }
  return present;
}

/**
 * Folds a number into a running hash.
 *
 * @param hash The hash so far.
 * @param value An integer.
 * @returns The new hash, a signed 32-bit integer.
 */
function mix(hash: number, value: number): number {
  return Math.imul(hash ^ value, 16777619);
}

/**
 * Adds an entity to the buffer when its feet lie inside the player's vision rectangle, the edge included, and, for a
 * kind shown only near, within `MINIMAP_NEAR_TILES` of the player's feet.
 *
 * @param others The buffer.
 * @param selfX The player's position across, in tiles.
 * @param selfY The player's position down, in tiles.
 * @param handle The entity's handle.
 * @param kind Its mark's kind.
 * @param x Its feet across, in tiles.
 * @param y Its feet down, in tiles.
 * @param name Its name, which only a player's mark shows.
 * @returns True when it was added; false outside vision, for a kind shown only near when it lies more than
 *   `MINIMAP_NEAR_TILES` from the player, and past the capacity, which `dropped` counts.
 */
export function addMinimapOther(others: MinimapOthers, selfX: number, selfY: number, handle: number, kind: number, x: number, y: number, name: EntityName): boolean {
  const dx = x - selfX;
  const dy = y - selfY;
  if (!inMinimapVision(dx, dy)) {
    return false;
  }
  if ((MINIMAP_OTHER_LOOKS[kind] as MinimapOtherLook).near && dx * dx + dy * dy > MINIMAP_NEAR_TILES * MINIMAP_NEAR_TILES) {
    return false;
  }
  const at = others.count;
  if (at >= others.handle.length) {
    others.dropped += 1;
    return false;
  }
  others.handle[at] = handle;
  others.kind[at] = kind;
  others.x[at] = x;
  others.y[at] = y;
  others.name[at] = name;
  others.count = at + 1;
  others.signature = mix(mix(mix(mix(others.signature, handle), kind), Math.round(x * 16)), Math.round(y * 16));
  return true;
}

/**
 * A signature of what a field would show of the buffer: the handle, kind and whole-pixel position of each mark whose
 * centre falls inside the field at this scale. It is equal for two frames that paint the same pixels, so a painter
 * repaints only when it moves.
 *
 * @param others The buffer.
 * @param selfX The player's position across, in tiles.
 * @param selfY The player's position down, in tiles.
 * @param width The field's width in logical pixels.
 * @param height Its height.
 * @param scale Logical pixels a tile.
 * @returns A signed 32-bit integer.
 */
export function minimapOthersSignature(others: MinimapOthers, selfX: number, selfY: number, width: number, height: number, scale: number): number {
  const centreX = Math.round(width / 2);
  const centreY = Math.round(height / 2);
  let hash = SEED;
  for (let i = 0; i < others.count; i += 1) {
    const px = Math.round(centreX + (others.x[i] as number - selfX) * scale);
    const py = Math.round(centreY + (others.y[i] as number - selfY) * scale);
    if (px < 0 || py < 0 || px >= width || py >= height) {
      continue;
    }
    hash = mix(mix(mix(mix(hash, others.handle[i] as number), others.kind[i] as number), px), py);
  }
  return hash;
}

/**
 * The width of a name's box: the small pixel face's advances of its characters and, after a space, of the family name,
 * less the clear column after the last glyph, with the one pixel outline each side.
 *
 * @param name The name.
 * @returns Logical pixels.
 */
function nameWidth(name: EntityName): number {
  return name.family.length === 0 ? smallTextBoxWidth(name.first) : smallTextWidth(name.first) + SMALL_SPACE + smallTextBoxWidth(name.family);
}

/**
 * Whether a name's box meets the box of the player's own disc or of any mark held, each taken as a 7 pixel square about
 * its centre (the widest mark the painter draws), the name's own mark included, which its gap clears.
 *
 * @param others The buffer.
 * @param selfX The player's position across, in tiles.
 * @param selfY The player's position down, in tiles.
 * @param centreX The field's centre across, in pixels.
 * @param centreY The field's centre down, in pixels.
 * @param scale Logical pixels a tile.
 * @param left The name box's left edge.
 * @param top Its top edge.
 * @param boxWidth Its width.
 * @returns True when the box overlaps one.
 */
function nameMeetsMark(others: MinimapOthers, selfX: number, selfY: number, centreX: number, centreY: number, scale: number, left: number, top: number, boxWidth: number): boolean {
  const half = (MARK_BOX - 1) / 2;
  if (left < centreX + half + 1 && centreX - half < left + boxWidth && top < centreY + half + 1 && centreY - half < top + NAME_HEIGHT) {
    return true;
  }
  for (let i = 0; i < others.count; i += 1) {
    const px = Math.round(centreX + ((others.x[i] as number) - selfX) * scale);
    const py = Math.round(centreY + ((others.y[i] as number) - selfY) * scale);
    if (left < px + half + 1 && px - half < left + boxWidth && top < py + half + 1 && py - half < top + NAME_HEIGHT) {
      return true;
    }
  }
  return false;
}

/**
 * Whether a name's box meets the box of a name already taken.
 *
 * @param names The places taken so far.
 * @param others The buffer.
 * @param count How many places are taken.
 * @param left The box's left edge.
 * @param top Its top edge.
 * @param boxWidth Its width.
 * @returns True when the box overlaps one.
 */
function nameMeetsName(names: MinimapNames, others: MinimapOthers, count: number, left: number, top: number, boxWidth: number): boolean {
  for (let taken = 0; taken < count; taken += 1) {
    const otherWidth = nameWidth(others.name[names.index[taken] as number] as EntityName);
    const otherLeft = names.flipped[taken] === 1 ? (names.anchor[taken] as number) - otherWidth : (names.anchor[taken] as number);
    const otherTop = names.top[taken] as number;
    if (left < otherLeft + otherWidth && otherLeft < left + boxWidth && top < otherTop + NAME_HEIGHT && otherTop < top + NAME_HEIGHT) {
      return true;
    }
  }
  return false;
}

/**
 * Chooses the names a field draws: the players' only, never a creature's. The nearest `MINIMAP_NAMES_MOST` players
 * whose marks lie in the field are taken nearest first, and one whose name would meet the name of a nearer player is
 * left out, so two names never overlap and the nearer player keeps theirs (the rule of the world map's names, ADR-100).
 * A name hangs to the right of its mark, or to the left of it when the mark lies in the field's right 45 percent, so none
 * is clipped. A name never covers the player's own disc or another mark: when its side is blocked it moves to the other
 * side if that lies inside the field and is free, else it is dropped (P1.9-T193). A box is estimated from the name's characters, so nothing is measured.
 *
 * @param names Where the places are written.
 * @param others The buffer.
 * @param selfX The player's position across, in tiles.
 * @param selfY The player's position down, in tiles.
 * @param width The field's width in logical pixels.
 * @param height Its height.
 * @param scale Logical pixels a tile.
 */
export function chooseOtherNames(names: MinimapNames, others: MinimapOthers, selfX: number, selfY: number, width: number, height: number, scale: number): void {
  const centreX = Math.round(width / 2);
  const centreY = Math.round(height / 2);
  const nearest = names.scratchIndex;
  const distance = names.scratchDistance;
  let held = 0;
  for (let i = 0; i < others.count; i += 1) {
    const length = (others.name[i] as EntityName).first.length;
    if (!isMinimapPlayer(others.kind[i] as number) || length === 0) {
      continue;
    }
    const dx = (others.x[i] as number) - selfX;
    const dy = (others.y[i] as number) - selfY;
    const px = Math.round(centreX + dx * scale);
    const py = Math.round(centreY + dy * scale);
    if (px < 0 || py < 0 || px >= width || py >= height) {
      continue;
    }
    const far = dx * dx + dy * dy;
    let at = held;
    while (at > 0 && (distance[at - 1] as number) > far) {
      at -= 1;
    }
    if (at >= MINIMAP_NAMES_MOST) {
      continue;
    }
    const last = Math.min(held, MINIMAP_NAMES_MOST - 1);
    for (let move = last; move > at; move -= 1) {
      distance[move] = distance[move - 1] as number;
      nearest[move] = nearest[move - 1] as number;
    }
    distance[at] = far;
    nearest[at] = i;
    held = Math.min(held + 1, MINIMAP_NAMES_MOST);
  }
  let count = 0;
  for (let rank = 0; rank < held; rank += 1) {
    const i = nearest[rank] as number;
    const name = others.name[i] as EntityName;
    const boxWidth = nameWidth(name);
    const px = Math.round(centreX + ((others.x[i] as number) - selfX) * scale);
    const py = Math.round(centreY + ((others.y[i] as number) - selfY) * scale);
    const top = py - Math.floor(NAME_HEIGHT / 2);
    const preferred = px > width * FLIP_AFTER;
    let placed = false;
    let flipped = preferred;
    let left = 0;
    for (let attempt = 0; attempt < 2 && !placed; attempt += 1) {
      flipped = attempt === 0 ? preferred : !preferred;
      left = flipped ? px - NAME_GAP - boxWidth : px + NAME_GAP;
      if (attempt === 1 && (left < 0 || left + boxWidth > width)) {
        continue;
      }
      placed = !nameMeetsMark(others, selfX, selfY, centreX, centreY, scale, left, top, boxWidth) && !nameMeetsName(names, others, count, left, top, boxWidth);
    }
    if (!placed) {
      continue;
    }
    names.index[count] = i;
    names.anchor[count] = flipped ? left + boxWidth : left;
    names.top[count] = top;
    names.flipped[count] = flipped ? 1 : 0;
    count += 1;
  }
  names.count = count;
}
